import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Orient, SourceScope } from '@photos/shared';

export const TABS = ['all', 'picks', 'years', 'regions', 'events', 'churches', 'groups'] as const;
export type LibraryTab = (typeof TABS)[number];
export type OrientFilter = Orient | 'all';

export interface LibraryState {
  source: SourceScope;
  tab: LibraryTab;
  tags: string[];
  orient: OrientFilter;
  picksOnly: boolean;
}

const SOURCE_KEY = 'photos.source';

/** A source value from a URL or storage; `archive` (Glean's old key, in old bookmarks) means `glean`. */
function sourceScope(v: string | null): SourceScope | null {
  if (v === 'archive') return 'glean';
  return v === 'glean' || v === 'flock' || v === 'all' ? v : null;
}

export function rememberedSource(): SourceScope {
  try {
    const v = sourceScope(localStorage.getItem(SOURCE_KEY));
    if (v) return v;
  } catch {
    /* storage unavailable */
  }
  return 'glean';
}

export function rememberSource(s: SourceScope) {
  try {
    localStorage.setItem(SOURCE_KEY, s);
  } catch {
    /* storage unavailable */
  }
}

export function parseLibraryParams(search: string, fallbackSource: SourceScope = 'glean'): LibraryState {
  const p = new URLSearchParams(search);
  const tab = p.get('tab');
  const orient = p.get('orient');
  return {
    source: sourceScope(p.get('source')) ?? fallbackSource,
    tab: (TABS as readonly string[]).includes(tab ?? '') ? (tab as LibraryTab) : 'all',
    tags: (p.get('tags') ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    orient: orient === 'h' || orient === 'v' || orient === 'sq' ? orient : 'all',
    picksOnly: p.get('picks') === '1',
  };
}

export function buildLibrarySearch(s: LibraryState): string {
  const p = new URLSearchParams();
  if (s.source !== 'glean') p.set('source', s.source);
  if (s.tab !== 'all') p.set('tab', s.tab);
  if (s.tags.length) p.set('tags', s.tags.join(','));
  if (s.orient !== 'all') p.set('orient', s.orient);
  if (s.picksOnly) p.set('picks', '1');
  const q = p.toString();
  return q ? `?${q}` : '';
}

/**
 * Library state lives in the URL on `/`. Elsewhere the last used source still applies so the theme and the sidebar
 * stay consistent.
 */
export function useLibraryState() {
  const location = useLocation();
  const navigate = useNavigate();
  const onLibrary = location.pathname === '/';
  const state = useMemo(
    () => (onLibrary ? parseLibraryParams(location.search, rememberedSource()) : { ...parseLibraryParams('', rememberedSource()) }),
    [onLibrary, location.search],
  );

  /** Go to the library with a new state. Filter changes scroll back to the top first (as the prototype). */
  const go = useCallback(
    (next: Partial<LibraryState>, opts: { replace?: boolean; keepScroll?: boolean } = {}) => {
      const merged: LibraryState = { ...state, ...next };
      if (next.tags) merged.tags = [...new Set(next.tags)];
      rememberSource(merged.source);
      if (!opts.keepScroll) window.scrollTo(0, 0);
      navigate({ pathname: '/', search: buildLibrarySearch(merged) }, { replace: opts.replace });
    },
    [navigate, state],
  );

  return { state, go, onLibrary };
}
